import React, { useState } from 'react';
import styles from './AddPermissionsModal.module.css';
import pageStyles from './PermissionsPage.module.css';
import {
  assignPermissionToRole,
  removePermissionFromRole,
  Role,
  Permission,
} from '../../services/roles.service';

interface AddPermissionsModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  roles: Role[];
  permissions: Permission[];
}

const AddPermissionsModal: React.FC<AddPermissionsModalProps> = ({
  open,
  onClose,
  onSave,
  roles,
  permissions,
}) => {
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [selectedPermissions, setSelectedPermissions] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  // Get current permissions for the selected role
  const getCurrentRolePermissions = (): number[] => {
    if (!selectedRole) return [];
    return selectedRole.permissions.map((p) => p.id);
  };

  const handlePermissionChange = (permissionId: number) => {
    const currentPermissions = getCurrentRolePermissions();
    const isCurrentlyAssigned = currentPermissions.includes(permissionId);

    setSelectedPermissions((prev) => {
      if (isCurrentlyAssigned) {
        // If permission is currently assigned, we're removing it
        return prev.filter((p) => p !== permissionId);
      } else {
        // If permission is not currently assigned, we're adding it
        return prev.includes(permissionId)
          ? prev.filter((p) => p !== permissionId)
          : [...prev, permissionId];
      }
    });
  };

  const handleSave = async () => {
    if (!selectedRole) {
      setError('Please select a role');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const currentPermissions = getCurrentRolePermissions();

      // Handle permissions to add (selected but not currently assigned)
      const permissionsToAdd = selectedPermissions.filter(
        (permId) => !currentPermissions.includes(permId),
      );

      // Handle permissions to remove (currently assigned but not selected)
      const permissionsToRemove = currentPermissions.filter(
        (permId) => !selectedPermissions.includes(permId),
      );

      // Add new permissions
      for (const permissionId of permissionsToAdd) {
        await assignPermissionToRole(selectedRole.id, permissionId);
      }

      // Remove permissions
      for (const permissionId of permissionsToRemove) {
        await removePermissionFromRole(selectedRole.id, permissionId);
      }

      setSelectedRole(null);
      setSelectedPermissions([]);
      onSave();
    } catch (err: any) {
      setError(err.message || 'Failed to update permissions');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedRole(null);
    setSelectedPermissions([]);
    setError(null);
    onClose();
  };

  if (!open) return null;

  // Filter permissions by search query
  const filteredPermissions = permissions.filter((perm) =>
    perm.name.toLowerCase().includes(search.toLowerCase()),
  );

  // Split permissions into 2 columns for grid layout
  const columns = 2;
  const rows = Math.ceil(filteredPermissions.length / columns);
  const permissionGrid = Array.from({ length: columns }, (_, col) =>
    filteredPermissions.slice(col * rows, (col + 1) * rows),
  );

  return (
    <div className={pageStyles.modalOverlay}>
      <div className={pageStyles.modalContent}>
        <button
          className={pageStyles.closeButton}
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
        <div className={styles.addPermissionsModalContent}>
          <div className={styles.modalHeader}>
            <h2 className={styles.addPermissionsTitle}>
              Manage Role Permissions
            </h2>
            <select
              className={styles.roleDropdown}
              value={selectedRole?.id || ''}
              onChange={(e) => {
                const role = roles.find(
                  (r) => r.id === parseInt(e.target.value),
                );
                setSelectedRole(role || null);
                // Initialize selected permissions with current role permissions
                if (role) {
                  setSelectedPermissions(role.permissions.map((p) => p.id));
                } else {
                  setSelectedPermissions([]);
                }
                setError(null);
              }}
              disabled={loading}
            >
              <option value="">Choose Role</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
            {/* Search input for filtering permissions */}
            <input
              type="text"
              placeholder="Search permissions..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={styles.permissionSearchInput}
            />
            {error && <p className={pageStyles.error}>{error}</p>}
          </div>

          <div className={styles.permissionGridWrapper}>
            {permissionGrid.map((col, colIdx) => (
              <div key={colIdx} className={styles.permissionGridColumn}>
                {col.map((perm) => (
                  <label
                    key={perm.id}
                    className={styles.permissionCheckboxLabel}
                  >
                    <input
                      type="checkbox"
                      checked={selectedPermissions.includes(perm.id)}
                      onChange={() => handlePermissionChange(perm.id)}
                      disabled={loading}
                    />
                    {perm.name}
                  </label>
                ))}
              </div>
            ))}
          </div>

          <div className={styles.modalButtonRow}>
            <button
              className={styles.saveButton}
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Permissions'}
            </button>
            <button
              className={styles.cancelButton}
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPermissionsModal;
